'use client';

import React from 'react';
import Link from 'next/link';
import SvgArrow from './SvgArrow';
import Skeleton from '@mui/material/Skeleton';
import Container from '@mui/material/Container';
import { JOB_SALARY_INSIGHT_ROUTER_PATH } from '@/constants/router-path';
import { useSingleLineVisible } from '@/utils/hooks/useSingleLineVisible';

const TopSection = (props: {
  navList: { name: string; href?: string }[];
  bgColor?: string;
  marginBottom?: string;
  title?: string;
  subTitle?: string;
  subTitleClassName?: string;
  cardList?: {
    title: string;
    content: string;
    subContent?: string;
    icon: React.ReactNode;
  }[];
  tags?: string[];
  subTags?: string[];
}) => {
  const {
    navList,
    title,
    subTitle,
    subTitleClassName = 'text-sm text-web-grey-600',
    cardList,
    marginBottom = 'mb-10',
    bgColor = 'bg-[linear-gradient(90.22deg,rgba(232,243,252,0.8)_0.18%,rgba(238,241,254,0.8)_100.46%)]',
    tags,
    subTags,
  } = props;

  const {
    wrapRef: subTagsWrapRef,
    measureRef: subTagMeasureRef,
    visibleCount: visibleSubTagCount,
  } = useSingleLineVisible({
    items: subTags,
  });

  return (
    <div className={`py-16 ${marginBottom} ${bgColor}`}>
      <Container
        className="!flex !gap-20 !items-center !justify-between"
        maxWidth="desktop"
      >
        <div className="grow">
          <div className="flex items-center gap-1">
            <Link
              href={JOB_SALARY_INSIGHT_ROUTER_PATH}
              className="hover:underline"
            >
              薪酬数据
            </Link>
            {navList.map(({ name, href }, index) => (
              <React.Fragment key={index}>
                <SvgArrow />
                {href ? (
                  <Link href={href} className="hover:underline">
                    {name}
                  </Link>
                ) : (
                  <span
                    className={
                      index === navList.length - 1 ? 'text-web-grey-600' : ''
                    }
                  >
                    {name}
                  </span>
                )}
              </React.Fragment>
            ))}
          </div>
          {tags && Array.isArray(tags) && tags.length > 0 && (
            <div className="flex items-center gap-4 mt-8">
              {tags.map((item, index) => (
                <span
                  key={index}
                  className="px-2 py-1 text-sm border border-web-grey-750 rounded-lg"
                >
                  {item}
                </span>
              ))}
            </div>
          )}
          {title && (
            <h1
              className={`text-3.25xl font-semibold ${tags && Array.isArray(tags) && tags.length > 0 ? 'mt-4' : 'mt-8'}`}
            >
              {title}
            </h1>
          )}
          {subTitle && (
            <p className={`${subTitleClassName} mt-4 line-clamp-3`}>
              {subTitle}
            </p>
          )}
          {subTags && Array.isArray(subTags) && subTags.length > 0 && (
            <>
              <div
                ref={subTagsWrapRef}
                className="flex items-center gap-4 mt-4"
              >
                {subTags.map((item, index) => (
                  <span
                    key={index}
                    hidden={index >= visibleSubTagCount}
                    className="px-2 py-1 text-sm border border-web-grey-750 rounded-lg"
                  >
                    {item}
                  </span>
                ))}
              </div>
              {!visibleSubTagCount && (
                <div className="grid grid-cols-4 gap-4">
                  <Skeleton variant="rounded" height={32} />
                  <Skeleton variant="rounded" height={32} />
                  <Skeleton variant="rounded" height={32} />
                </div>
              )}
              <span
                ref={subTagMeasureRef}
                className="fixed left-[-9999px] top-[-9999px] px-2 py-1 text-sm border border-web-grey-750 rounded-lg whitespace-nowrap pointer-events-none"
                aria-hidden="true"
              />
            </>
          )}
        </div>
        <div className="grid grid-cols-2 gap-6 shrink-0">
          {cardList?.map(({ title, content, subContent, icon }, index) => (
            <div
              key={index + title}
              className="shadow-3xl px-5 py-4 rounded-lg min-w-[260px] bg-white text-xs text-web-grey-600"
            >
              <p className="flex items-center justify-between">
                <span className="font-semibold">{title}</span>
                {icon}
              </p>
              <p className="text-primary font-semibold text-2xl mt-4">
                {content}
              </p>
              {subContent && <p className="mt-1">{subContent}</p>}
            </div>
          ))}
        </div>
      </Container>
    </div>
  );
};

export default TopSection;
